/**
 * ProseMirror class composition for `<MarkdownEditor>`.
 *
 * Extracted from the editor's `editorProps.attributes` so the surface
 * scoping is testable on its own — the composition is small but it is
 * the hook a stylesheet keys off, and a silent change to it is invisible
 * in TypeScript. See `__tests__/wiki-doc-surface-scope.test.ts`.
 */

import type { EditorSurface } from './extensions'

export interface EditorProseMirrorClassInput {
  surface: EditorSurface
  chrome: 'card' | 'flush'
  /** Card-modal display mode — strips the editor-internal padding. */
  borderless: boolean
}

/**
 * `wiki-doc-surface` marks the full-page wiki doc editor so CSS that
 * belongs to THAT editor (the 30vh edit-mode typing room in
 * `global.css`) can key off the editor itself.
 *
 * It deliberately does NOT key off the `.wiki-editor` ancestor: that
 * class sits on the doc-card `<article>`, which also wraps the
 * page-comments section, so a descendant selector reaches the comment
 * composers too — which is exactly the bug this replaced (an empty
 * page-comment composer opened at its 304px max height, scrolling from
 * the first keystroke).
 */
export function buildEditorProseMirrorClass({
  surface,
  chrome,
  borderless,
}: EditorProseMirrorClassInput): string {
  // `chrome === 'flush'` (wiki) strips the horizontal padding so the prose
  // left edge sits flush with the title input above it, and bumps to
  // `prose-base` for a full-page doc editor; the dense `prose-sm` is right
  // for chat / cards but reads tight at 760px wide.
  const base =
    borderless || chrome === 'flush'
      ? chrome === 'flush'
        ? 'prose dark:prose-invert prose-base max-w-none focus:outline-none py-2 text-fg break-words'
        : 'prose dark:prose-invert prose-sm max-w-none focus:outline-none text-fg break-words'
      : 'prose dark:prose-invert prose-sm max-w-none focus:outline-none px-3 py-2 text-fg break-words'

  return surface === 'wiki' ? `${base} wiki-doc-surface` : base
}
